import React, { useEffect, useState, useCallback } from "react";
import { Alert, Spinner, ProgressBar } from "react-bootstrap";
import {
  AlertCircle,
  AlertTriangle,
  PartyPopper,
  Layers,
} from "lucide-react";
import apiClient from "../../utils/apiClient";
import SyllabusDayCard from "./SyllabusDayCard";
import MapRecordingModal from "./MapRecordingModal";
import MapMaterialModal from "../Materials/MapMaterialModal";
import "./syllabus.scss";

/**
 * TrainerTodaySession
 *
 * Shows the FULL syllabus timeline for a batch.
 * - Current (first non-completed) day is auto-expanded
 * - Completed days collapsed with summary
 * - Future days collapsed
 * - Supports: start, end, restart, reopen, toggle topic, map recording
 *
 * Props:
 *   - batchId : String (batch ObjectId)
 *   - batchTiming : Object { startTime, endTime } (optional)
 */
const TrainerTodaySession = ({ batchId, batchTiming }) => {
  const [syllabusData, setSyllabusData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [actionLoading, setActionLoading] = useState(null); // dayNumber of the card currently loading

  // Track which cards are expanded — auto-expand current day
  const [expandedDays, setExpandedDays] = useState(new Set());

  // Map-recording modal state
  const [mapModal, setMapModal] = useState({
    show: false,
    dayNumber: null,
    topicIndex: null,
    topicName: "",
  });

  // Map-material modal state
  const [matModal, setMatModal] = useState({
    show: false,
    dayNumber: null,
    topicIndex: null,
    topicName: "",
  });

  // Linked material counts per day/topic: { [dayNumber]: { [topicIndex]: count } }
  const [linkedMaterialCounts, setLinkedMaterialCounts] = useState({});

  // ── Fetch syllabus ──
  const fetchSyllabus = useCallback(async (silent = false) => {
    if (!silent) setLoading(true);
    try {
      const { data } = await apiClient.get(
        `/syllabus/batch/${batchId}/syllabus`
      );
      setSyllabusData(data);
      setError("");

      // Auto-expand the current day (first non-completed)
      if (data.sessions?.length) {
        const currentDay = data.sessions.find((s) => s.status !== "completed");
        if (currentDay) {
          setExpandedDays((prev) => {
            const next = new Set(prev);
            next.add(currentDay.dayNumber);
            return next;
          });
        }
      }
    } catch (err) {
      const status = err?.response?.status;
      const msg = err?.response?.data?.message || "";
      if (status === 404) {
        setSyllabusData(null);
        setError(
          msg.includes("Batch")
            ? "Batch not found."
            : "Syllabus not added for this course yet."
        );
      } else {
        setSyllabusData(null);
        setError(msg || "Failed to load syllabus.");
      }
    } finally {
      setLoading(false);
    }
  }, [batchId]);

  useEffect(() => {
    fetchSyllabus();
  }, [fetchSyllabus]);

  // ── Fetch linked material counts per day ──
  const fetchLinkedMaterialCounts = useCallback(async () => {
    if (!syllabusData?.sessions?.length) return;
    try {
      const counts = {};
      await Promise.all(
        syllabusData.sessions.map(async (session) => {
          try {
            const { data } = await apiClient.get(
              `/materials/batch/${batchId}/by-syllabus-day/${session.dayNumber}`
            );
            const materials = data.data || [];
            const topicCounts = {};
            materials.forEach((m) => {
              const idx = m.linkedTopicIndex ?? -1;
              topicCounts[idx] = (topicCounts[idx] || 0) + 1;
            });
            counts[session.dayNumber] = topicCounts;
          } catch { /* ignore per-day errors */ }
        })
      );
      setLinkedMaterialCounts(counts);
    } catch { /* ignore */ }
  }, [batchId, syllabusData?.sessions]);

  useEffect(() => {
    fetchLinkedMaterialCounts();
  }, [fetchLinkedMaterialCounts]);

  // ── Determine current day ──
  const currentDayNumber =
    syllabusData?.sessions?.find((s) => s.status !== "completed")?.dayNumber ?? null;

  const allCompleted =
    syllabusData?.sessions?.length > 0 &&
    syllabusData.sessions.every((s) => s.status === "completed");

  // ── Toggle expand/collapse ──
  const toggleExpand = (dayNumber) => {
    setExpandedDays((prev) => {
      const next = new Set(prev);
      if (next.has(dayNumber)) next.delete(dayNumber);
      else next.add(dayNumber);
      return next;
    });
  };

  // ── API action wrapper ──
  const withAction = async (dayNumber, apiCall) => {
    setActionLoading(dayNumber);
    setError("");
    try {
      await apiCall();
      await fetchSyllabus(true); // silent refresh
    } catch (err) {
      setError(err?.response?.data?.message || "Action failed.");
    } finally {
      setActionLoading(null);
    }
  };

  // ── Session actions ──
  const handleStart = (dayNumber) =>
    withAction(dayNumber, async () => {
      const { data } = await apiClient.post(
        `/syllabus/batch/${batchId}/syllabus/${dayNumber}/start`
      );
      // Zoom meeting is started by the backend — navigate to classroom
      if (data.zoomMode === "v2") {
        window.location.href = `/i/classroom/${batchId}`;
      }
    });

  const handleEnd = (dayNumber) =>
    withAction(dayNumber, () =>
      apiClient.post(`/syllabus/batch/${batchId}/syllabus/${dayNumber}/end`)
    );

  const handleRestart = (dayNumber) =>
    withAction(dayNumber, () =>
      apiClient.post(`/syllabus/batch/${batchId}/syllabus/${dayNumber}/restart`)
    );

  const handleReopen = (dayNumber) =>
    withAction(dayNumber, () =>
      apiClient.post(`/syllabus/batch/${batchId}/syllabus/${dayNumber}/reopen`)
    );

  const handleMarkCompleted = (dayNumber) =>
    withAction(dayNumber, () =>
      apiClient.post(`/syllabus/batch/${batchId}/syllabus/${dayNumber}/mark-completed`)
    );

  // ── Topic toggle ──
  const handleToggleTopic = (dayNumber, topicIndex) =>
    withAction(dayNumber, () =>
      apiClient.post(
        `/syllabus/batch/${batchId}/syllabus/${dayNumber}/topic/${topicIndex}/toggle`
      )
    );

  // ── Map recording modal ──
  const handleOpenMap = (dayNumber, topicIndex, topicName) => {
    setMapModal({ show: true, dayNumber, topicIndex, topicName });
  };

  const handleMapRecording = async (recordingId) => {
    if (mapModal.dayNumber == null) return;
    try {
      await apiClient.post(
        `/syllabus/batch/${batchId}/syllabus/${mapModal.dayNumber}/topic/${mapModal.topicIndex}/recording`,
        { recordingId }
      );
      setMapModal({ show: false, dayNumber: null, topicIndex: null, topicName: "" });
      await fetchSyllabus(true);
    } catch (err) {
      throw err; // let modal handle display
    }
  };

  // ── Map material modal ──
  const handleOpenMatMap = (dayNumber, topicIndex, topicName) => {
    setMatModal({ show: true, dayNumber, topicIndex, topicName });
  };

  const handleLinkMaterial = async (materialId) => {
    if (matModal.dayNumber == null) return;
    try {
      await apiClient.put(`/materials/${materialId}/link-syllabus-topic`, {
        syllabusDay: matModal.dayNumber,
        topicIndex: matModal.topicIndex,
      });
      setMatModal({ show: false, dayNumber: null, topicIndex: null, topicName: "" });
      await fetchLinkedMaterialCounts();
    } catch (err) {
      throw err; // let modal handle display
    }
  };

  // ── Loading ──
  if (loading) {
    return (
      <div className="trainer-session-loading">
        <Spinner animation="border" size="sm" />
        <span>Loading syllabus…</span>
      </div>
    );
  }

  // ── No sessions ──
  if (!syllabusData || !syllabusData.sessions || syllabusData.sessions.length === 0) {
    return (
      <Alert variant="info" className="trainer-session-alert">
        <AlertCircle size={18} className="me-2" />
        {error || "Batch has not been started yet. Syllabus sessions will appear once the batch is started."}
      </Alert>
    );
  }

  // ── Error fallback ──
  if (error && !syllabusData) {
    return (
      <Alert variant="warning" className="trainer-session-alert">
        <AlertTriangle size={18} className="me-2" />
        {error}
      </Alert>
    );
  }

  return (
    <div className="trainer-today-session">
      {/* Dismissible error */}
      {error && (
        <Alert variant="danger" dismissible onClose={() => setError("")} className="mb-3">
          {error}
        </Alert>
      )}

      {/* Overall progress bar */}
      <div className="syllabus-overall-progress mb-3">
        <div className="sop-meta">
          <span className="sop-label">
            <Layers size={15} />
            Overall Progress
          </span>
          <span className="sop-value">
            {syllabusData.completedDays}/{syllabusData.totalDays} days
            {" "}({syllabusData.progressPercent}%)
          </span>
        </div>
        <ProgressBar
          now={syllabusData.progressPercent}
          variant={allCompleted ? "success" : "primary"}
          className="sop-bar"
        />
      </div>

      {/* All completed banner */}
      {allCompleted && (
        <Alert variant="success" className="trainer-session-alert trainer-session-complete mb-3">
          <PartyPopper size={18} className="me-2" />
          All syllabus sessions completed! 🎉
        </Alert>
      )}

      {/* Day cards timeline */}
      <div className="syllabus-timeline">
        {syllabusData.sessions.map((session) => (
          <SyllabusDayCard
            key={session.dayNumber}
            session={session}
            isCurrent={session.dayNumber === currentDayNumber}
            isExpanded={expandedDays.has(session.dayNumber)}
            onToggleExpand={() => toggleExpand(session.dayNumber)}
            onStart={() => handleStart(session.dayNumber)}
            onEnd={() => handleEnd(session.dayNumber)}
            onRestart={() => handleRestart(session.dayNumber)}
            onReopen={() => handleReopen(session.dayNumber)}
            onMarkCompleted={() => handleMarkCompleted(session.dayNumber)}
            onToggleTopic={(topicIndex) => handleToggleTopic(session.dayNumber, topicIndex)}
            onMapRecording={(topicIndex, topicName) =>
              handleOpenMap(session.dayNumber, topicIndex, topicName)
            }
            onMapMaterial={(topicIndex, topicName) =>
              handleOpenMatMap(session.dayNumber, topicIndex, topicName)
            }
            linkedMaterials={linkedMaterialCounts[session.dayNumber] || {}}
            actionLoading={actionLoading === session.dayNumber}
            batchTiming={batchTiming}
          />
        ))}
      </div>

      {/* Map recording modal */}
      <MapRecordingModal
        show={mapModal.show}
        batchId={batchId}
        topicName={mapModal.topicName}
        onMap={handleMapRecording}
        onClose={() =>
          setMapModal({ show: false, dayNumber: null, topicIndex: null, topicName: "" })
        }
      />

      {/* Link material modal */}
      <MapMaterialModal
        show={matModal.show}
        batchId={batchId}
        targetLabel={matModal.topicName}
        alreadyLinkedIds={[]}
        onLink={handleLinkMaterial}
        onClose={() =>
          setMatModal({ show: false, dayNumber: null, topicIndex: null, topicName: "" })
        }
      />
    </div>
  );
};

export default TrainerTodaySession;
